import React, { useState, useMemo } from 'react';
import { calculateComplianceScore } from '../../utils/Nutrition';
import { getAvatarVariantUrl } from '../../utils/avatarVariants';
import { isActivityExpired } from './Activity';
import './DojoPulse.css';

/**
 * DojoPulse — ambient inline card showing dojo member activity for a given day.
 * 
 * Collapsed: overlapping avatar bubbles + summary text + live indicator
 * Expanded: per-member collapsible sections with volume/calorie stats
 * 
 * Sits inside each day group in the personal "You" feed.
 */

const TYPE_META = {
  workout: { icon: '🏋️', label: 'Workout' },
  meal:    { icon: '🍽️', label: 'Meal' },
  media:   { icon: '📸', label: 'Media' },
  metric:  { icon: '📊', label: 'Metrics' },
  survey:  { icon: '📝', label: 'Check-in' },
};

const getTypeIcon = (type) => (TYPE_META[type] || { icon: '📌' }).icon;
const getTypeLabel = (type) => (TYPE_META[type] || { label: 'Activity' }).label;

/** Check if an activity was edited (updated) less than 15 minutes ago */
const isRecentlyEdited = (activity) => {
  const updatedAt = activity.updated_at || activity.completed_at || activity.created_at;
  if (!updatedAt) return false;
  return (Date.now() - new Date(updatedAt).getTime()) < 15 * 60 * 1000;
};

const isOngoing = (activity) => {
  if (!activity.status || activity.status === 'complete' || isActivityExpired(activity)) return false;
  return ['in_progress', 'ongoing', 'started'].includes(activity.status) || isRecentlyEdited(activity);
};

/** Relative time string from a date — e.g. "3m ago", "2h ago", "now" */
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) return '';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return '';
};

const timeUntil = (dateStr) => {
  if (!dateStr) return '';
  const diff = new Date(dateStr).getTime() - Date.now();
  if (diff < 0) return '';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'due soon';
  if (mins < 60) return `due in ${mins}m`;
  const hrs = Math.floor(mins / 60);
  return `due in ${hrs}h`;
};

/** Short detail line for an activity — exercise count, food count, etc. */
const getActivityDetail = (activity) => {
  const d = activity.data;
  if (!d) return null;
  if (activity.type === 'workout') {
    const exCount = Array.isArray(d.exercises) ? d.exercises.length : 0;
    const setCount = Array.isArray(d.exercises)
      ? d.exercises.reduce((s, ex) => s + (Array.isArray(ex.sets) ? ex.sets.length : 0), 0)
      : 0;
    if (exCount > 0) return `${exCount} exercise${exCount > 1 ? 's' : ''}${setCount > 0 ? ` · ${setCount} set${setCount > 1 ? 's' : ''}` : ''}`;
  }
  if (activity.type === 'meal') {
    const foodCount = Array.isArray(d.foods) ? d.foods.length : 0;
    if (foodCount > 0) return `${foodCount} item${foodCount > 1 ? 's' : ''}`;
  }
  if (activity.type === 'media') {
    const fileCount = Array.isArray(d.files) ? d.files.length : 0;
    if (fileCount > 0) return `${fileCount} file${fileCount > 1 ? 's' : ''}`;
  }
  return null;
};

/** Compute per-user daily stats: total volume across workouts, total calories across meals, calorie compliance score */
const computeUserStats = (activities, user) => {
  let totalVolume = 0;
  let totalCalories = 0;
  let totalProtein = 0;

  activities.forEach(a => {
    if (a.type === 'workout' && a.volume_summary?.total_volume) {
      totalVolume += a.volume_summary.total_volume;
    }
    if (a.type === 'meal' && a.nutrition_summary) {
      totalCalories += a.nutrition_summary.calories || 0;
      totalProtein += a.nutrition_summary.protein || 0;
    }
  });

  const targets = user.nutrition_targets || null;
  let calorieScore = null;
  if (targets && totalCalories > 0) {
    calorieScore = calculateComplianceScore(
      { calories: Math.round(totalCalories), protein: Math.round(totalProtein) },
      targets
    );
  }

  return {
    totalVolume: Math.round(totalVolume),
    totalCalories: Math.round(totalCalories),
    calorieScore,
    targets,
  };
};

/** Collapsible per-member section with stats in header */
const MemberSection = ({ user, activities, stats, memberIdx, onUserClick, onActivityClick }) => {
  const [memberExpanded, setMemberExpanded] = useState(false);
  const hasOngoingActivity = activities.some(isOngoing);

  const { totalVolume, totalCalories, calorieScore, targets } = stats;

  // Calorie score badge color — matches DailyNutritionSummary logic
  let badgeColor = '#10b981';
  let badgeBg = 'rgba(16, 185, 129, 0.2)';
  if (calorieScore !== null && calorieScore < 80) {
    const calMax = targets?.calories_max ?? targets?.calories ?? 2000;
    if (totalCalories > calMax) {
      badgeColor = '#ef4444';
      badgeBg = 'rgba(239, 68, 68, 0.2)';
    } else {
      badgeColor = '#ffffff';
      badgeBg = 'rgba(255, 255, 255, 0.15)';
    }
  }

  return (
    <div 
      className={`dojo-pulse-member ${memberExpanded ? 'expanded' : ''}`}
      style={{ animationDelay: `${memberIdx * 50}ms` }}
    >
      <div 
        className="dojo-pulse-member-header"
        onClick={(e) => { e.stopPropagation(); setMemberExpanded(prev => !prev); }}
      >
        <div className="dojo-pulse-member-left">
          <div 
            className="dojo-pulse-member-identity"
            onClick={(e) => { e.stopPropagation(); onUserClick && onUserClick(user); }}
          >
            {user.avatar_url ? (
              <img src={getAvatarVariantUrl(user, 'xs')} alt="" className="dojo-pulse-member-avatar" />
            ) : (
              <div className="dojo-pulse-member-avatar-placeholder">
                {(user.display_name || '?')[0].toUpperCase()}
              </div>
            )}
            <span className="dojo-pulse-member-name">{user.display_name || 'Member'}</span>
            {hasOngoingActivity && <span className="dojo-pulse-member-live-dot" />}
          </div>
          <div className="dojo-pulse-member-stats">
            {totalVolume > 0 && (
              <span className="dojo-pulse-stat volume">
                +{totalVolume.toLocaleString()}
              </span>
            )}
            {calorieScore !== null && (
              <span
                className="dojo-pulse-stat score"
                style={{ 
                  background: badgeBg, 
                  color: badgeColor,
                  fontSize: '0.7rem',
                  padding: '1px 6px',
                  borderRadius: '12px',
                  fontWeight: 500
                }}
              >
                {calorieScore}% on target
              </span>
            )}
          </div>
        </div>
        <div className="dojo-pulse-member-right">
          <span className="dojo-pulse-member-count">{activities.length}</span>
          <span className={`dojo-pulse-member-chevron ${memberExpanded ? 'expanded' : ''}`}>‹</span>
        </div>
      </div>

      {memberExpanded && (
        <div className="dojo-pulse-activities">
          {activities.map((activity, actIdx) => {
            const ongoing = isOngoing(activity);
            const expired = isActivityExpired(activity);
            const completed = activity.status === 'complete';
            const showLive = ongoing || (isRecentlyEdited(activity) && !expired);
            const detail = getActivityDetail(activity);
            
            let timeStr = '';
            if (!completed && !expired && activity.due_at) {
              timeStr = timeUntil(activity.due_at);
            }
            if (!timeStr) {
              timeStr = timeAgo(ongoing ? activity.created_at : (activity.completed_at || activity.created_at));
            }

            const pts = activity.source?.consistency_points || 0;
            return (
              <div
                key={activity._id}
                className={`dojo-pulse-activity ${ongoing ? 'ongoing' : expired ? 'expired' : completed ? 'completed' : ''}`}
                onClick={(e) => { e.stopPropagation(); onActivityClick && onActivityClick(activity, user); }}
                style={{ animationDelay: `${actIdx * 30}ms` }}
              >
                <div className="dojo-pulse-activity-left">
                  <span className="dojo-pulse-activity-icon">{getTypeIcon(activity.type)}</span>
                  <div className="dojo-pulse-activity-info">
                    <span className="dojo-pulse-activity-name">
                      {activity.name || getTypeLabel(activity.type)}
                    </span>
                    {detail && <span className="dojo-pulse-activity-detail">{detail}</span>}
                    {activity.dojo_announcement && (
                      <span className="dojo-pulse-activity-announcement">
                        {activity.dojo_announcement_emoji || '🥋'} {activity.dojo_announcement}
                      </span>
                    )}
                  </div>
                </div>
                <div className="dojo-pulse-activity-right">
                  {expired ? (
                    <span className="dojo-pulse-activity-expired-mark">✗</span>
                  ) : completed ? (
                    <span className="dojo-pulse-activity-check">✓</span>
                  ) : showLive ? (
                    <span className="dojo-pulse-activity-live">
                      <span className="dojo-pulse-live-dot" />
                      Live
                    </span>
                  ) : pts > 0 ? (
                    <span className="dojo-pulse-activity-pts">+{pts}</span>
                  ) : null}
                  {timeStr && <span className="dojo-pulse-activity-time">{timeStr}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DojoPulse = ({ dojoActivities, dojoUsers, dojoName, onUserClick, onActivityClick }) => {
  const [expanded, setExpanded] = useState(false);

  // Group activities by user and compute stats
  const memberData = useMemo(() => {
    if (!dojoActivities || dojoActivities.length === 0) return [];

    const userMap = {};
    (dojoUsers || []).forEach(u => { userMap[u._id || u.user_id] = u; });

    const byUser = {};
    dojoActivities.forEach(a => {
      const uid = a.user_id?.toString() || a.user_id;
      if (!byUser[uid]) byUser[uid] = [];
      byUser[uid].push(a);
    });

    return Object.entries(byUser).map(([userId, acts]) => {
      const user = userMap[userId] || { _id: userId, display_name: 'Dojo member' };
      const typeOrder = ['workout', 'meal', 'media', 'metric', 'survey', 'other'];
      const sortWithinType = (a, b) => {
        if (a.source?.plan_order !== undefined && b.source?.plan_order !== undefined) {
          return a.source.plan_order - b.source.plan_order;
        }
        const aTime = a.due_at ? new Date(a.due_at).getTime() : Infinity;
        const bTime = b.due_at ? new Date(b.due_at).getTime() : Infinity;
        if (aTime !== bTime) return aTime - bTime;
        const aCreated = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bCreated = b.created_at ? new Date(b.created_at).getTime() : 0;
        return aCreated - bCreated;
      };
      const sortedActs = [...acts].sort((a, b) => {
        const aTypeIdx = typeOrder.indexOf(a.type);
        const bTypeIdx = typeOrder.indexOf(b.type);
        const aIdx = aTypeIdx === -1 ? typeOrder.indexOf('other') : aTypeIdx;
        const bIdx = bTypeIdx === -1 ? typeOrder.indexOf('other') : bTypeIdx;
        if (aIdx !== bIdx) return aIdx - bIdx;
        return sortWithinType(a, b);
      });
      return {
        user,
        activities: sortedActs,
        stats: computeUserStats(sortedActs, user),
      };
    }).sort((a, b) => {
      // Members with ongoing activities first, then by activity count
      const aOngoing = a.activities.some(isOngoing) ? 1 : 0;
      const bOngoing = b.activities.some(isOngoing) ? 1 : 0;
      if (bOngoing !== aOngoing) return bOngoing - aOngoing;
      return b.activities.length - a.activities.length;
    });
  }, [dojoActivities, dojoUsers]);

  if (memberData.length === 0) return null;

  const totalMembers = memberData.length;
  const totalActivities = dojoActivities.length;
  const ongoingCount = dojoActivities.filter(isOngoing).length;
  const hasOngoing = ongoingCount > 0;

  // Build a richer summary
  const typeCounts = {};
  dojoActivities.forEach(a => { typeCounts[a.type] = (typeCounts[a.type] || 0) + 1; });
  const summaryChips = Object.entries(typeCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([type, count]) => ({ type, count, icon: getTypeIcon(type) }));

  return (
    <div className={`dojo-pulse ${expanded ? 'expanded' : ''} ${hasOngoing ? 'has-ongoing' : ''}`}>
      {/* Left accent bar */}
      <div className="dojo-pulse-accent" />

      {/* Collapsed strip */}
      <div 
        className="dojo-pulse-header"
        onClick={() => setExpanded(prev => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(prev => !prev); }}
      >
        <div className="dojo-pulse-left">
          <div className="dojo-pulse-avatars">
            {memberData.slice(0, 5).map(({ user }, idx) => (
              <div 
                key={user._id}
                className="dojo-pulse-avatar-wrapper"
                style={{ zIndex: 5 - idx }}
              >
                {user.avatar_url ? (
                  <img src={getAvatarVariantUrl(user, 'xs')} alt="" className="dojo-pulse-avatar" />
                ) : (
                  <div className="dojo-pulse-avatar-placeholder">
                    {(user.display_name || '?')[0].toUpperCase()}
                  </div>
                )}
              </div>
            ))}
            {totalMembers > 5 && (
              <div className="dojo-pulse-avatar-wrapper" style={{ zIndex: 0 }}>
                <div className="dojo-pulse-avatar-placeholder overflow">
                  +{totalMembers - 5}
                </div>
              </div>
            )}
          </div>
          <div className="dojo-pulse-summary-block">
            <span className="dojo-pulse-title">
              {dojoName || 'Dojo'}
              {hasOngoing && <span className="dojo-pulse-live-badge">LIVE</span>}
            </span>
            <span className="dojo-pulse-summary">
              {totalMembers} member{totalMembers > 1 ? 's' : ''} · {totalActivities} activit{totalActivities === 1 ? 'y' : 'ies'}
            </span>
          </div>
        </div>
        <div className="dojo-pulse-right">
          <div className="dojo-pulse-type-pills">
            {summaryChips.slice(0, 3).map(({ type, count, icon }) => (
              <span key={type} className="dojo-pulse-type-pill">
                <span className="dojo-pulse-type-pill-icon">{icon}</span>
                <span className="dojo-pulse-type-pill-count">{count}</span>
              </span>
            ))}
          </div>
          <span className={`dojo-pulse-chevron ${expanded ? 'expanded' : ''}`}>‹</span>
        </div>
      </div>

      {/* Expanded member sections */}
      {expanded && (
        <div className="dojo-pulse-detail">
          {memberData.map(({ user, activities, stats }, memberIdx) => (
            <MemberSection
              key={user._id}
              user={user}
              activities={activities}
              stats={stats}
              memberIdx={memberIdx}
              onUserClick={onUserClick}
              onActivityClick={onActivityClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DojoPulse;

// Shared helpers for DojoHub
export {
  TYPE_META,
  getTypeIcon,
  getTypeLabel,
  isOngoing,
  isRecentlyEdited,
  timeAgo,
  timeUntil,
  getActivityDetail,
  computeUserStats,
  MemberSection,
};
